import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { User, Globe, Lock, ArrowRight, LogIn, Loader2, CheckCircle2 } from 'lucide-react';
import Logo from '../components/Logo';
import HeroSection from '../components/HeroSection';
import { auth, googleProvider } from '../services/firebase';
import { signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { apiService } from '../services/apiService';
import { API_BASE_URL } from '../config/api';
import { usePortfolio } from '../context/PortfolioContext';

interface AuthPageProps {
  formData: any;
  updateFormData: (data: any) => void;
  handleRegister: () => void;
  handleLogin: (email: string, pass: string) => Promise<string>;
  setStep?: (step: number) => void;
  initialMode?: 'register' | 'login';
}

import { useSEO } from '../hooks/useSEO';

const AuthPage: React.FC<AuthPageProps> = ({ formData, updateFormData, handleRegister, handleLogin, setStep, initialMode = 'login' }) => {
  const navigate = useNavigate();
  const { clearSessionCache } = usePortfolio();
  const [mode, setMode] = useState<'register' | 'login'>(initialMode);

  useSEO({
    title: mode === 'register' ? 'Register Account' : 'Sign In',
    description: 'Kelola akun CatatCrypto Anda. Lakukan registrasi atau masuk untuk mengelola portofolio, AI trading assistant, dan histori transaksi Anda.',
    keywords: 'login, register, sign in, signup, catatcrypto auth'
  });

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref') || params.get('referral');
    if (refCode) {
      updateFormData({ referralCode: refCode.trim().toUpperCase() });
    }
  }, [updateFormData]);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; email?: string; password?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successInfo, setSuccessInfo] = useState({ title: '', message: '' });

  const validateFields = () => {
    const errors: { name?: string; email?: string; password?: string } = {};

    if (mode === 'register' && (!formData.name || !formData.name.trim())) {
      errors.name = 'Full Name is required.';
    }

    if (!formData.email || !formData.email.trim()) {
      errors.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address (e.g. name@example.com).';
    }

    if (!formData.password) {
      errors.password = 'Password is required.';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long.';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 2FA Verification States
  const [show2FAVerify, setShow2FAVerify] = useState(false);
  const [temp2FAToken, setTemp2FAToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);

  const handleOTPVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      setOtpError('Kode OTP harus 6-digit angka');
      return;
    }
    setOtpError('');
    setIsVerifyingOTP(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/login/two-factor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          token: temp2FAToken,
          code: otpCode
        })
      });

      const data = await response.json();
      if (response.ok) {
        clearSessionCache();
        localStorage.setItem('has_cex_accounts', data.has_cex_accounts ? 'true' : 'false');
        localStorage.setItem('auth_token', data.token);
        updateFormData({ 
          userId: data.user.id, 
          name: data.user.name, 
          email: data.user.email,
          token: data.token
        });

        // Restore profile if exists
        if (data.profile && Object.keys(data.profile).length > 0) {
          updateFormData({
            traderType: data.profile.trader_type,
            gender: data.profile.gender,
            birthYear: data.profile.birth_year,
            goal: data.profile.primary_goal,
            sources: data.profile.acquisition_sources,
            plan: data.profile.subscription_plan,
            broker: data.profile.broker,
            method: data.profile.sync_method,
          });
        }

        setSuccessInfo({ 
          title: 'Login Successful!', 
          message: `Welcome back! Redirecting...` 
        });
        setShowSuccess(true);
        await new Promise(resolve => setTimeout(resolve, 600));

        const hasProfile = data.profile && Object.keys(data.profile).length > 0;
        if (data.has_cex_accounts || hasProfile) {
          window.location.href = '/dashboard';
        } else {
          localStorage.setItem('app_step', '2');
          if (setStep) setStep(2);
          window.location.href = '/onboarding';
        }
      } else {
        setOtpError(data.message || 'Incorrect verification code.');
      }
    } catch (err) {
      console.error(err);
      setOtpError('Failed to verify OTP. Check your internet connection.');
    } finally {
      setIsVerifyingOTP(false);
    }
  };

  const handleSubmit = async () => {
    if (isLoading || showSuccess) return;
    setError('');

    if (!validateFields()) {
      return;
    }

    setIsLoading(true);
    
    try {
      if (mode === 'register') {
        // 1. Verify email with backend first (checks duplicates & disposable domains)
        await handleRegister();

        // 2. Create user in Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        
        // 3. Send email verification
        await sendEmailVerification(userCredential.user);

        // 4. Get ID Token
        const idToken = await userCredential.user.getIdToken();

        // 5. Send to Go backend to create user
        const data = await apiService.loginWithFirebase(idToken, formData.referralCode);

        // 6. Save token & user info
        localStorage.setItem('auth_token', data.token);
        updateFormData({ 
          userId: data.user.id, 
          name: formData.name || data.user.name, 
          email: data.user.email,
          token: data.token
        });

        setSuccessInfo({ 
          title: 'Account Created!', 
          message: `Verification link sent to ${formData.email}. Please verify your email.` 
        });
        setShowSuccess(true);
        await new Promise(resolve => setTimeout(resolve, 800));
        navigate('/verify-email');
      } else {
        // 1. Sign in with Firebase Auth
        const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);

        // 2. Get ID Token
        const idToken = await userCredential.user.getIdToken();

        // 3. Authenticate with Go backend
        const data = await apiService.loginWithFirebase(idToken);

        if (data.requires_2fa) {
          setTemp2FAToken(data.token);
          setShow2FAVerify(true);
          setIsLoading(false);
          return;
        }

        // 4. Save credentials
        clearSessionCache();
        localStorage.setItem('has_cex_accounts', data.has_cex_accounts ? 'true' : 'false');
        localStorage.setItem('auth_token', data.token);
        updateFormData({ 
          userId: data.user.id, 
          name: data.user.name, 
          email: data.user.email,
          token: data.token
        });

        // Restore profile if exists
        if (data.profile && Object.keys(data.profile).length > 0) {
          updateFormData({
            traderType: data.profile.trader_type,
            gender: data.profile.gender,
            birthYear: data.profile.birth_year,
            goal: data.profile.primary_goal,
            sources: data.profile.acquisition_sources,
            plan: data.profile.subscription_plan,
            broker: data.profile.broker,
            method: data.profile.sync_method,
          });
        }

        setSuccessInfo({ 
          title: 'Login Successful!', 
          message: `Welcome back! Redirecting...` 
        });
        setShowSuccess(true);
        await new Promise(resolve => setTimeout(resolve, 600));

        // 5. Check if email is verified
        if (!data.email_verified) {
          window.location.href = '/verify-email';
          return;
        }

        const hasProfile = data.profile && Object.keys(data.profile).length > 0;
        if (data.has_cex_accounts || hasProfile) {
          window.location.href = '/dashboard';
        } else {
          localStorage.setItem('app_step', '2');
          if (setStep) setStep(2);
          window.location.href = '/onboarding';
        }
      }
    } catch (err: any) {
      console.error("Auth Error:", err);
      let errorMsg = err.message || 'Authentication failed.';
      if (err.code === 'auth/email-already-in-use') {
        errorMsg = 'This email is already registered.';
      } else if (err.code === 'auth/invalid-credential') {
        errorMsg = 'Incorrect email or password.';
      } else if (err.code === 'auth/weak-password') {
        errorMsg = 'Password must be at least 6 characters.';
      }
      setError(errorMsg);
      setIsLoading(false);
    }
  };

  const handleGoogleSignInClick = async () => {
    if (isLoading || showSuccess) return;
    
    let userCredential;
    try {
      userCredential = await signInWithPopup(auth, googleProvider);
    } catch (err: any) {
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Google Sign-In popup was closed before completing authentication.');
      } else {
        setError(err.message || 'Google Sign-In failed.');
      }
      return;
    }
    
    setError('');
    setIsLoading(true);
    
    try {
      const idToken = await userCredential.user.getIdToken();
      const data = await apiService.loginWithGoogle(idToken, formData.referralCode);
      
      if (data.requires_2fa) {
        setTemp2FAToken(data.token);
        setShow2FAVerify(true);
        setIsLoading(false);
        return;
      }
      
      clearSessionCache();
      localStorage.setItem('has_cex_accounts', data.has_cex_accounts ? 'true' : 'false');
      localStorage.setItem('auth_token', data.token);
      
      updateFormData({
        userId: data.user.id,
        name: data.user.name,
        email: data.user.email,
        token: data.token
      });
      
      setSuccessInfo({
        title: 'Login Successful!',
        message: `Welcome back, ${data.user.name}! Redirecting...`
      });
      setShowSuccess(true);
      await new Promise(resolve => setTimeout(resolve, 600));
      
      const hasProfile = data.profile && Object.keys(data.profile).length > 0;
      if (data.has_cex_accounts || hasProfile) {
        window.location.href = '/dashboard';
      } else {
        localStorage.setItem('app_step', '2');
        if (setStep) setStep(2);
        window.location.href = '/onboarding';
      }
    } catch (err: any) {
      console.error("Google Auth Error:", err);
      setError(err.message || 'Google Sign-In failed.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex select-none relative overflow-hidden">
      <HeroSection />

      {/* Success Popup */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              className="bg-neutral-900 border border-neutral-800 p-8 rounded-3xl text-center max-w-sm w-full mx-4 shadow-2xl"
            >
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 size={40} className="text-green-500" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-white">{successInfo.title}</h2>
              <p className="text-neutral-400 mb-6 font-medium">{successInfo.message}</p>
              <div className="flex justify-center">
                <div className="w-12 h-1 bg-neutral-800 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ x: '-100%' }}
                    animate={{ x: '0%' }}
                    transition={{ duration: 0.5, ease: "linear" }}
                    className="w-full h-full bg-green-500"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="flex-1 flex flex-col p-6 lg:p-12 max-w-md mx-auto w-full relative justify-center">
        <Logo size="lg" centered />
        
        {show2FAVerify ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-5 max-w-sm mx-auto w-full"
          >
            <header>
              <h1 className="text-2xl font-bold text-white mb-1">Two-Factor Verification</h1>
              <p className="text-neutral-400 text-sm">
                Masukkan 6-digit kode OTP dari aplikasi authenticator Anda untuk melanjutkan login.
              </p>
            </header>

            {otpError && (
              <div className="p-3 bg-red-900/20 border border-red-900/30 rounded-lg text-red-500 text-xs font-bold">
                {otpError}
              </div>
            )}

            <form onSubmit={handleOTPVerify} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-neutral-300 uppercase tracking-wide">OTP Code</label>
                <input 
                  type="text" 
                  maxLength={6}
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="Contoh: 123456"
                  className="w-full bg-neutral-900 border border-neutral-800 rounded-lg py-3 text-center text-lg focus:outline-none focus:border-red-600 transition-colors tracking-widest font-mono font-bold"
                />
              </div>

              <button 
                type="submit"
                disabled={isVerifyingOTP || otpCode.length !== 6}
                className="w-full py-3 bg-red-700 hover:bg-red-600 text-white font-bold rounded-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed text-sm"
              >
                {isVerifyingOTP ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <>Verify & Login <ArrowRight size={18} /></>
                )}
              </button>

              <button 
                type="button"
                onClick={() => { setShow2FAVerify(false); setError(''); }}
                className="w-full py-2 bg-neutral-950 hover:bg-neutral-900 border border-neutral-900 text-neutral-400 hover:text-white rounded-lg text-xs font-bold transition-all"
              >
                Back to Login
              </button>
            </form>
          </motion.div>
        ) : (
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4 max-w-sm mx-auto w-full"
          >
            <header>
            <h1 className="text-2xl font-bold text-white mb-1">
              {mode === 'register' ? 'Create Account' : 'Welcome Back'}
            </h1>
            <p className="text-neutral-400 text-sm">
              {mode === 'register' 
                ? 'Fill in the details to start your trading journal.' 
                : 'Login with your credentials to access your dashboard.'}
            </p>
          </header>

          <AnimatePresence mode="wait">
            {error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="p-3 bg-red-900/20 border border-red-900/30 rounded-lg text-red-500 text-xs font-bold"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="space-y-3">
            {mode === 'register' && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-1 overflow-hidden"
              >
                <label className="text-xs font-medium text-neutral-300 uppercase tracking-wide">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" size={16} />
                  <input 
                    type="text" 
                    placeholder="John Doe"
                    disabled={isLoading}
                    className={`w-full bg-neutral-900 border ${fieldErrors.name ? 'border-red-500 bg-red-950/10' : 'border-neutral-800 focus:border-red-600'} rounded-lg py-2.5 pl-10 pr-3 text-sm outline-none transition-colors disabled:opacity-50`}
                    value={formData.name}
                    onChange={(e) => {
                      updateFormData({ name: e.target.value });
                      if (fieldErrors.name) setFieldErrors(prev => ({ ...prev, name: undefined }));
                    }}
                  />
                </div>
                {fieldErrors.name && (
                  <p className="text-red-500 text-[11px] font-sans font-bold pl-1 mt-1 flex items-center gap-1">
                    <span>⚠️</span> {fieldErrors.name}
                  </p>
                )}
              </motion.div>
            )}

            <div className="space-y-1">
              <label className="text-xs font-medium text-neutral-300 uppercase tracking-wide">Email</label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" size={16} />
                <input 
                  type="email" 
                  placeholder="name@example.com"
                  disabled={isLoading}
                  className={`w-full bg-neutral-900 border ${fieldErrors.email ? 'border-red-500 bg-red-950/10' : 'border-neutral-800 focus:border-red-600'} rounded-lg py-2.5 pl-10 pr-3 text-sm outline-none transition-colors disabled:opacity-50`}
                  value={formData.email}
                  onChange={(e) => {
                    updateFormData({ email: e.target.value });
                    if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: undefined }));
                  }}
                />
              </div>
              {fieldErrors.email && (
                <p className="text-red-500 text-[11px] font-sans font-bold pl-1 mt-1 flex items-center gap-1">
                  <span>⚠️</span> {fieldErrors.email}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-neutral-300 uppercase tracking-wide">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" size={16} />
                <input 
                  type="password" 
                  placeholder="**********"
                  disabled={isLoading}
                  className={`w-full bg-neutral-900 border ${fieldErrors.password ? 'border-red-500 bg-red-950/10' : 'border-neutral-800 focus:border-red-600'} rounded-lg py-2.5 pl-10 pr-3 text-sm outline-none transition-colors disabled:opacity-50`}
                  value={formData.password}
                  onChange={(e) => {
                    updateFormData({ password: e.target.value });
                    if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: undefined }));
                  }}
                />
              </div>
              {fieldErrors.password && (
                <p className="text-red-500 text-[11px] font-sans font-bold pl-1 mt-1 flex items-center gap-1">
                  <span>⚠️</span> {fieldErrors.password}
                </p>
              )}
            </div>
            {mode === 'register' && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-1 overflow-hidden"
              >
                <label className="text-xs font-medium text-neutral-300 uppercase tracking-wide">Referral Code (Optional)</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" size={16} />
                  <input 
                    type="text" 
                    placeholder="Enter referral code"
                    disabled={isLoading}
                    className="w-full bg-neutral-900 border border-neutral-800 rounded-lg py-2.5 pl-10 pr-3 text-sm focus:outline-none focus:border-red-600 transition-colors disabled:opacity-50 uppercase font-mono tracking-wider"
                    value={formData.referralCode || ''}
                    onChange={(e) => updateFormData({ referralCode: e.target.value.toUpperCase().replace(/\s/g, '') })}
                  />
                </div>
              </motion.div>
            )}
          </div>

          <button 
            onClick={handleSubmit}
            disabled={isLoading}
            className="w-full py-3 bg-red-700 hover:bg-red-600 text-white font-bold rounded-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed text-sm"
          >
            {isLoading ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              <>
                {mode === 'register' ? 'Create Account' : 'Sign In'} 
                {mode === 'register' ? <ArrowRight size={18} /> : <LogIn size={18} />}
              </>
            )}
          </button>

          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-neutral-800" /></div>
            <div className="relative flex justify-center text-[10px] uppercase"><span className="bg-black px-2 text-neutral-500 font-bold">Or continue with</span></div>
          </div>

          <button 
            onClick={handleGoogleSignInClick}
            disabled={isLoading} 
            className="w-full py-2.5 bg-neutral-900 border border-neutral-800 hover:bg-neutral-800 text-white rounded-lg flex items-center justify-center gap-3 transition-colors disabled:opacity-50 text-sm cursor-pointer"
          >
            <img src="https://www.google.com/favicon.ico" className="w-4 h-4" alt="Google" referrerPolicy="no-referrer" />
            {mode === 'register' ? 'Sign up with Google' : 'Sign in with Google'}
          </button>

          <p className="text-center text-neutral-400 text-xs">
            {mode === 'register' ? 'Already have an account?' : "Don't have an account?"}{' '}
            <span 
              onClick={() => { 
                if (!isLoading) { 
                  const targetMode = mode === 'register' ? 'login' : 'register';
                  setMode(targetMode); 
                  setError(''); 
                  navigate(targetMode === 'register' ? '/register' : '/login');
                } 
              }}
              className="text-red-500 font-bold cursor-pointer hover:underline"
            >
              {mode === 'register' ? 'Sign in' : 'Register now'}
            </span>
          </p>
          </motion.div>
        )}
        <footer className="mt-6 py-4 text-center border-t border-neutral-900">
           <p className="text-[9px] uppercase tracking-widest text-neutral-700 font-black">© 2026 Catat Crypto. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default AuthPage;
